import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Fab, useMediaQuery, useTheme } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';

/**
 * Floating "HOME" button with a blue house icon that navigates to the dashboard.
 * Shown on all routes except the dashboard itself.
 */
const HomeButton: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktopCoordinatorMioOrario = !isMobile
    && location.pathname === '/mio-orario'
    && (() => {
      try { return JSON.parse(localStorage.getItem('userInfo') || '{}')?.ruolo?.toLowerCase() === 'coordinatore'; }
      catch { return false; }
    })();

  // Fade on scroll only for Mio Orario mobile
  const isMioOrarioRoute = location.pathname === '/mio-orario';
  const [visible, setVisible] = useState(true);
  const lastYRef = useRef<number>(0);

  useEffect(() => {
    if (!(isMioOrarioRoute && isMobile)) return;

    // Initialize last scroll position on mount
    lastYRef.current = window.scrollY || 0;
    let ticking = false;

    const onScroll = () => {
      const currentY = window.scrollY || 0;
      // throttle via rAF to avoid excessive setState
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const delta = currentY - lastYRef.current;
          // If scrolling down, hide; if scrolling up, show
          if (delta > 2) {
            setVisible(false);
          } else if (delta < -2) {
            setVisible(true);
          }
          lastYRef.current = currentY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [isMioOrarioRoute, isMobile]);

  // Hide on the dashboard to avoid redundancy
  if (location.pathname === '/dashboard' || isDesktopCoordinatorMioOrario) return null;

  return (
    <Fab
      variant="extended"
      onClick={() => navigate('/dashboard')}
      aria-label="Home"
      sx={{
        position: 'fixed',
        top: 8,
        left: 16,
        zIndex: 1500,
        bgcolor: '#e3f2fd', // light azure background
        color: '#1976d2', // primary azure text
        fontWeight: 700,
        '&:hover': { bgcolor: '#bbdefb' },
        // Fade/slide only on Mio Orario mobile
        opacity: isMioOrarioRoute && isMobile ? (visible ? 1 : 0) : 1,
        transform: isMioOrarioRoute && isMobile ? (visible ? 'translateY(0)' : 'translateY(-8px)') : 'none',
        transition: isMioOrarioRoute && isMobile ? 'opacity 200ms ease, transform 200ms ease' : undefined,
        pointerEvents: isMioOrarioRoute && isMobile ? (visible ? 'auto' : 'none') : 'auto',
      }}
   >
      <HomeIcon sx={{ mr: 1, color: '#1976d2' }} />
      HOME
    </Fab>
  );
};

export default HomeButton;
